import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { browserTimezone } from '../utils/companyDate';

// Plans a visitor can arrive with from /pricing (audit B2 / Finding 2.8c).
// Every trial starts on the same footing regardless of choice - this only
// records which plan they came to buy so we know what to set up for them.
const PLAN_NAMES = {
  basic: 'Basic',
  pro: 'Pro',
  premium: 'Premium',
  enterprise: 'Enterprise',
};

// The signup product email checkbox wording (owner decision 2026-10-22).
export const MARKETING_OPT_IN_LABEL = 'Email me SnowTrack product news, tips and offers. You can unsubscribe at any time.';

function Register() {
  const { register, error } = useAuth();
  const { getAppName } = useConfig();
  const [searchParams] = useSearchParams();

  // ?plan=<tier> from the pricing page CTA. Anything unrecognised is ignored.
  const planParam = (searchParams.get('plan') || '').toLowerCase();
  const requestedPlan = PLAN_NAMES[planParam] ? planParam : null;
  const [formData, setFormData] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    // Product email (migration 111). Unchecked: consent is never assumed.
    marketingOptIn: false,
  });
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    });
    // Clear password error when user types
    if (e.target.name === 'password' || e.target.name === 'confirmPassword') {
      setPasswordError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (formData.password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    await register({
      companyName: formData.companyName,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      password: formData.password,
      marketingOptIn: formData.marketingOptIn === true,
      requestedPlan,
      // The company starts in the browser's zone when it is on the list; the
      // server uses the default otherwise. Changeable on the Getting started
      // card and in Settings, Company.
      timezone: browserTimezone(),
    });

    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-logo">{getAppName()}</h1>
          <p className="auth-subtitle">Free trial, 1 storm event included</p>
        </div>

        {requestedPlan && (
          <div className="alert alert-info">
            You picked the <strong>{PLAN_NAMES[requestedPlan]}</strong> plan. Your trial starts
            free either way, and you can pay for it whenever you are ready.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="companyName">Company Name</label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              className="form-input"
              value={formData.companyName}
              onChange={handleChange}
              placeholder="Your Business Name"
              required
            />
          </div>

          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label" htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                className="form-input"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="John"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                className="form-input"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Smith"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              className="form-input"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@company.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="phone">Phone</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              className="form-input"
              value={formData.phone}
              onChange={handleChange}
              placeholder="(555) 123-4567"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              className="form-input"
              value={formData.password}
              onChange={handleChange}
              placeholder="At least 8 characters"
              required
              autoComplete="new-password"
              onBlur={(e) => {
                if (e.target.value && e.target.value.length < 8) {
                  setPasswordError('Password must be at least 8 characters');
                }
              }}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              className="form-input"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your password"
              required
              autoComplete="new-password"
            />
            {passwordError && (
              <p className="form-error">{passwordError}</p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="marketingOptIn" style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', cursor: 'pointer', fontSize: '14px' }}>
              <input
                type="checkbox"
                id="marketingOptIn"
                name="marketingOptIn"
                checked={formData.marketingOptIn}
                onChange={handleChange}
                style={{ marginTop: '3px' }}
              />
              <span>{MARKETING_OPT_IN_LABEL}</span>
            </label>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block btn-lg"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Start Free Trial'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
